// Mednos App-Switcher — gemeinsame Implementierung für alle Apps.
//
// Eine Quelldatei, von jedem App-Frontend als ES-Modul importiert (Vite bündelt
// sie mit): das Panel sieht in jeder App identisch aus (fixes dunkles Theme,
// unabhängig vom App-Theme). Der Offen-Zustand liegt in einem Cookie auf
// .mednos.eu und überlebt damit den Wechsel zwischen den Subdomain-Apps —
// das Panel bleibt offen, bis es aktiv geschlossen wird. Im lokalen Dev
// (localhost) ersetzt localStorage das Domain-Cookie.
//
// Verwendung:
//   import { initAppSwitcher } from '../../platform/shared/app-switcher.js'
//   initAppSwitcher({ appKey: 'radiology' })

const APPS = {
  radiology: {
    name: 'Mednos Radiology',
    host: 'radiology.mednos.eu',
    prodUrl: 'https://radiology.mednos.eu/',
    devUrl: 'http://localhost:5174/',
    beschreibung: 'Radiologische Befundung',
    accent: '#8b8d98',
  },
  study: {
    name: 'Mednos Study',
    host: 'study.mednos.eu',
    prodUrl: 'https://study.mednos.eu/',
    devUrl: 'http://localhost:5173/',
    beschreibung: 'Spaced-Repetition-Lernen',
    accent: '#A78BFA',
  },
  papers: {
    name: 'Mednos Papers',
    host: 'papers.mednos.eu',
    prodUrl: 'https://papers.mednos.eu/',
    devUrl: 'http://localhost:5175/',
    beschreibung: 'Literatur-Radar',
    // Bewusst ohne `accent`: Papers laeuft im transparenten Skin, dort soll
    // der Panel-Name dieselbe Textfarbe tragen wie der Rest des Panels —
    // schwarz im Light-, weiss im Dark-Theme statt Petrol in beiden.
    // Pilotphase: aus dem Registry-Ausfall-Fallback der ANDEREN Apps
    // herausfiltern; Papers selbst rendert sich weiterhin ueber APPS.
    // Entfaellt zusammen mit PAPERS_PILOT_USERS am Ende der Pilotphase.
    pilot: true,
  },
};

const REGISTRY_URL = 'https://mednos.eu/api/apps';
// Dieselbe Bedingung wie der Touch-Block im CSS oben.
const TOUCH_MQ = '(hover: none) and (pointer: coarse)';
const OPEN_FLAG = 'mednos_switch_open';
// Zuletzt geöffnete App — mednos.eu leitet nach dem Login dorthin weiter,
// statt eine Übersichtsseite zu zeigen. Domain-Cookie wie OPEN_FLAG, damit
// es über alle *.mednos.eu-Subdomains gilt; ein Jahr Laufzeit, weil es reine
// Bequemlichkeit ist und ein Ablauf nur wieder im Default landen würde.
const LAST_APP_FLAG = 'mednos_last_app';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2.5" y="3.5" width="19" height="17" rx="3" fill="none" stroke="currentColor" stroke-width="1.75"/><rect class="app-switch-fill" x="5" y="6.5" width="3.5" height="11" rx="1" fill="currentColor"/></svg>';

// 3×3-Kachel-Grid (netz.svg) — Klickziel für das Apps-Panel im Panel-Kopf.
const GRID_ICON =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4.5,17.5H2a2,2,0,0,0-2,2V22a2,2,0,0,0,2,2H4.5a2,2,0,0,0,2-2V19.5A2,2,0,0,0,4.5,17.5Zm0,4.5H2V19.5H4.5Z"/><path d="M22,17.5H19.5a2,2,0,0,0-2,2V22a2,2,0,0,0,2,2H22a2,2,0,0,0,2-2V19.5A2,2,0,0,0,22,17.5ZM22,22H19.5V19.5H22Z"/><path d="M4.5,8.75H2a2,2,0,0,0-2,2v2.5a2,2,0,0,0,2,2H4.5a2,2,0,0,0,2-2v-2.5A2,2,0,0,0,4.5,8.75Zm0,4.5H2v-2.5H4.5Z"/><path d="M22,8.75H19.5a2,2,0,0,0-2,2v2.5a2,2,0,0,0,2,2H22a2,2,0,0,0,2-2v-2.5A2,2,0,0,0,22,8.75Zm0,4.5H19.5v-2.5H22Z"/><path d="M4.5,0H2A2,2,0,0,0,0,2V4.5a2,2,0,0,0,2,2H4.5a2,2,0,0,0,2-2V2A2,2,0,0,0,4.5,0Zm0,4.5H2V2H4.5Z"/><path d="M13.25,17.5h-2.5a2,2,0,0,0-2,2V22a2,2,0,0,0,2,2h2.5a2,2,0,0,0,2-2V19.5A2,2,0,0,0,13.25,17.5Zm0,4.5h-2.5V19.5h2.5Z"/><path d="M13.25,8.75h-2.5a2,2,0,0,0-2,2v2.5a2,2,0,0,0,2,2h2.5a2,2,0,0,0,2-2v-2.5A2,2,0,0,0,13.25,8.75Zm0,4.5h-2.5v-2.5h2.5Z"/><path d="M13.25,0h-2.5a2,2,0,0,0-2,2V4.5a2,2,0,0,0,2,2h2.5a2,2,0,0,0,2-2V2A2,2,0,0,0,13.25,0Zm0,4.5h-2.5V2h2.5Z"/><path d="M22,0H19.5a2,2,0,0,0-2,2V4.5a2,2,0,0,0,2,2H22a2,2,0,0,0,2-2V2A2,2,0,0,0,22,0Zm0,4.5H19.5V2H22Z"/></svg>';

// Fixe dunkle Palette — bewusst KEINE App-CSS-Variablen, damit das Panel in
// jeder App (auch im Study-Light-Theme) exakt gleich aussieht.
const CSS = `
/* Docked-Layout: kein Backdrop — das offene Panel schiebt die App nach
   rechts, sein rechter Rand ist die neue linke Screen-Kante der App.
   Die Panelbreite steht den Apps als --mednos-sidepanel-w zur Verfügung
   (z. B. für Full-Bleed-Elemente, die sonst mit 100vw rechnen). */
:root {
  --mednos-sidepanel-w: min(calc(17rem + 7mm), 82vw);
  /* Ruhe-Breite des Panels = Icon-Spalte + rechts derselbe Abstand, den die
     Buttons zum linken Screen-Rand haben (1rem + 2px) — die Trennlinie sitzt
     damit symmetrisch zur linken Kante. */
  --app-switch-rail-w: calc(1rem + 2px + 1.6rem + 1rem + 2px);
  /* Hoehe der Bodenleiste auf Touch-Geraeten. Oeffentlich wie
     --mednos-sidepanel-w: Apps richten ihre bodennahen schwebenden Elemente
     daran aus, statt die Zahl abzuschreiben. */
  --mednos-bottombar-h: 3.5rem;
  /* Unterkante der festen Icon-Spalte (Startoffset + zwei Icon-Zeilen +
     Zeilenabstand), gemessen ab Viewport-Oberkante. Apps rücken damit auf
     schmalen Screens ihren Inhalt darunter, statt ihn unter den Icons
     verschwinden zu lassen. */
  --app-switch-rail-h: calc(1rem + 8px + 3.5rem + 0.25rem + 10px);
}
body.app-switch-host { transition: margin-left 0.22s ease; }
/* Abgangs-Fade beim App-Wechsel: ganze Seite (inkl. Panel) kurz Richtung
   Schwarz ausblenden, bevor navigiert wird. */
html.app-switch-leaving { background: #000; }
html.app-switch-leaving body { transition: opacity 0.15s ease; opacity: 0; }
body.app-switch-host.app-switch-noanim { transition: none; }
body.app-switch-open, body.app-switcher-open { margin-left: var(--mednos-sidepanel-w); }
/* In-Place-Expansion: das Panel startet in Breite der Icon-Spalte (bis zur
   rechten Kante der Corner-Buttons) und fährt von dort nach rechts aus —
   es zieht also NICHT von links über die Buttons, sondern beginnt rechts
   neben ihnen; die Labels werden wie ein aufgehender Vorhang freigegeben.
   Die Icon-Spalte liegt exakt auf der App-eigenen Corner-Spalte
   (viewport-left calc(1rem + 2px), erste Zeile top calc(1rem + 8px + Nudge),
   Zeilen-Pitch --app-switch-pitch). visibility versteckt Border + Schatten
   im Ruhezustand. */
.app-switch-panel {
  --app-switch-pitch: calc(1.6rem + 0.25rem + 10px);
  position: fixed; top: 0; left: 0; height: 100vh;
  width: var(--app-switch-rail-w); overflow: hidden;
  background: #000; border-right: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.4);
  visibility: hidden;
  transition: width 0.22s ease, visibility 0s 0.22s;
  z-index: 9999;
  color: #fff; font-family: 'Inter', system-ui, sans-serif;
}
.app-switch-panel.is-open {
  width: var(--mednos-sidepanel-w);
  visibility: visible;
  transition: width 0.22s ease, visibility 0s;
}
.app-switch-panel.no-anim,
.app-switch-panel.no-anim .app-switch-actions,
.app-switch-panel.no-anim .app-switch-panel__head { transition: none; }
/* Choreografie beim Öffnen: Die Aktions-Zeilen starten einen Zeilen-Pitch
   höher — die Apps-Zeile liegt damit auf der Trigger-Position, Einstellungen
   auf dem Gear usw. — und wandern beim Ausfahren nach unten auf ihre
   endgültigen Plätze, während der Brand-Name oben einblendet. */
.app-switch-panel .app-switch-actions {
  transform: translateY(calc(-1 * var(--app-switch-pitch)));
  transition: transform 0.22s ease;
}
.app-switch-panel.is-open .app-switch-actions { transform: translateY(0); }
.app-switch-panel .app-switch-panel__head { opacity: 0; transition: opacity 0.22s ease; }
.app-switch-panel.is-open .app-switch-panel__head { opacity: 1; }
/* Diskrete vertikale Trennlinie an der Icon-Spalten-Kante — auch bei
   geschlossenem Panel sichtbar. Beim Öffnen verschwindet sie SOFORT (die
   Panel-Border startet an exakt derselben Position und wandert nach rechts —
   optisch "übernimmt" sie die Linie); beim Schließen erscheint sie erst
   wieder, wenn das Panel zugefahren ist (0.22s Delay). */
.app-switch-rail-line {
  position: fixed; top: 0; left: var(--app-switch-rail-w); height: 100vh;
  width: 1px; background: rgba(255, 255, 255, 0.1);
  z-index: 90; pointer-events: none;
  opacity: 1; transition: opacity 0s 0.22s;
}
body.app-switch-open .app-switch-rail-line,
body.app-switcher-open .app-switch-rail-line { opacity: 0; transition: opacity 0s; }
/* Innerer Wrapper in fester Panelbreite, links verankert: der Inhalt bricht
   während der Breiten-Animation nicht um, er wird nur rechts beschnitten.
   padding-left/-top = Corner-Spalten-Position der Apps; --app-switch-nudge
   (via opts.topNudge) gleicht app-spezifische Vertikal-Offsets aus. */
.app-switch-panel__inner {
  position: absolute; top: 0; left: 0; bottom: 0;
  width: var(--mednos-sidepanel-w); box-sizing: border-box;
  display: flex; flex-direction: column; gap: calc(0.25rem + 10px);
  padding: calc(1rem + 8px + var(--app-switch-nudge, 0px)) 4mm 24px calc(1rem + 2px);
}
/* Kopfzeile: Brand-Name linksbündig auf der Icon-Spalte (direkt über dem
   Apps-Grid-Icon), Schließen-Button rechts. Fest 1.6rem hoch — der Name
   ragt vertikal über die Zeile hinaus, ohne die Zeilen zu verschieben. */
.app-switch-panel__head { display: flex; align-items: center; height: 1.6rem; flex-shrink: 0; }
.app-switch-brand-name {
  min-width: 0;
  font-family: 'PF Videotext', 'PFVideoText', sans-serif;
  font-size: 1.25rem; letter-spacing: 1px; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  /* Optische Zentrierung in der Kopfzeile, aus den PF-Videotext-Metriken:
     Versalien (0–658/1000 em) sitzen 0.126em unter der Line-Box-Mitte
     (Ascent 1160, Descent 250) — Unterlängen (g/y) hängen erwartungsgemäß
     darunter. */
  transform: translateY(-0.126em);
}
.app-switch-close {
  flex-shrink: 0; margin-left: auto;
  display: inline-flex; align-items: center; justify-content: center;
  width: 1.6rem; height: 1.6rem; padding: 0; border: none; background: transparent;
  color: #fff; cursor: pointer; border-radius: 0.5rem;
  transition: opacity 0.15s;
}
.app-switch-close:hover { opacity: 0.65; }
.app-switch-close svg { display: block; width: 1.25rem; height: 1.25rem; }
/* Aktions-Zeilen im Corner-Spalten-Raster: Icon 1.6rem auf der Spalten-
   position, Zeilen-Pitch 1.6rem + 14px. Das Hover-Highlight ragt via
   Padding + exakt gegenläufigem Margin über die Zeile hinaus, ohne den
   Pitch zu verändern; der Hover-Rand ist ein inset box-shadow statt eines
   Borders, weil echte Border-Breiten auf Device-Pixel gerundet werden und
   die Margin-Kompensation sonst um Subpixel verrutscht. */
.app-switch-actions { display: flex; flex-direction: column; gap: calc(0.25rem + 10px); }
/* Zeilen mit isVisible() werden per [hidden] aus-/eingeblendet — die
   display:flex-Regel unten würde die UA-Regel [hidden]{display:none} sonst
   überstimmen. */
.app-switch-action[hidden] { display: none; }
.app-switch-action-group[hidden] { display: none; }
/* Knopf + Fortschrittszeile als ein Flex-Kind: der Balken haengt am Knopf,
   nicht eine volle Zeilenluecke darunter. */
.app-switch-action-group { display: flex; flex-direction: column; gap: 8px; }
.app-switch-action {
  display: flex; align-items: center; gap: 1rem;
  padding: 8px 12px; margin: -8px -12px;
  border: none; border-radius: 10px; background: transparent;
  color: #fff; cursor: pointer; text-align: left; font-family: inherit;
  transition: background 0.15s, box-shadow 0.15s;
}
/* Hover-Kachel bewusst ohne Rand — nur die Fläche hebt sich ab */
.app-switch-action:hover {
  background: rgba(255, 255, 255, 0.05);
}
.app-switch-action__icon {
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 1.6rem; height: 1.6rem; color: #fff;
}
.app-switch-action__icon svg { display: block; width: 1.2rem; height: 1.2rem; overflow: visible; }
.app-switch-action__label { font-size: 0.9rem; font-weight: 500; white-space: nowrap; }
/* Fortschritt einer laufenden Aktion (z. B. Papers-Sync): duenner Balken und
   eine Zeile Text, beides linksbuendig unter dem Label statt unter dem Icon. */
.app-switch-progress {
  display: flex; flex-direction: column; gap: 5px;
  padding-left: calc(1.6rem + 1rem);
}
.app-switch-progress[hidden] { display: none; }
.app-switch-progress__bar {
  height: 3px; border-radius: 2px; overflow: hidden;
  background: rgba(255, 255, 255, 0.16);
}
.app-switch-progress__bar > i {
  display: block; height: 100%; width: 0;
  background: #fff; transition: width 0.3s ease;
}
/* Unbestimmt: solange der Lauf noch nicht weiss, wie viel er zu tun hat,
   wandert ein kurzes Stueck statt eines stehenden Nullbalkens. */
.app-switch-progress__bar.is-unbestimmt > i {
  width: 35%; transition: none;
  animation: app-switch-wandert 1.2s ease-in-out infinite;
}
@keyframes app-switch-wandert {
  from { margin-left: -35%; }
  to { margin-left: 100%; }
}
.app-switch-progress__text {
  font-size: 0.75rem; color: rgba(255, 255, 255, 0.72);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* Laufende Aktion: das eigene Icon dreht sich. */
.app-switch-action.is-busy .app-switch-action__icon svg {
  animation: app-switch-dreht 1.1s linear infinite;
}
@keyframes app-switch-dreht {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .app-switch-action.is-busy .app-switch-action__icon svg,
  .app-switch-progress__bar.is-unbestimmt > i { animation: none; }
}
/* Apps-Popup: kein zweites Sidepanel mehr, sondern ein schwebendes Popover
   rechts neben dem Panel — Oberkante bündig mit der Oberkante des
   Sidepanel-Buttons (erste Icon-Zeile, 1rem + 8px). Blendet mit kurzem
   Slide/Fade ein. */
.app-switch-apps {
  position: fixed;
  left: calc(var(--mednos-sidepanel-w) + 12px);
  top: calc(1rem + 8px);
  width: 17rem;
  display: flex; flex-direction: column; gap: 4px; padding: 10px;
  /* Gleicher Look wie das Radiology-Einstellungs-Overlay: #1a1a1a-Fläche,
     12px-Radius, weicher Schatten, ohne Border */
  background: #1a1a1a;
  border: none;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  opacity: 0; visibility: hidden; transform: translateX(-8px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0s 0.18s;
  z-index: 10000;
  color: #fff; font-family: 'Inter', system-ui, sans-serif;
}
.app-switch-apps.is-open {
  opacity: 1; visibility: visible; transform: translateX(0);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0s;
}
.app-switch-list { display: flex; flex-direction: column; gap: 4px; }
.app-switch-item {
  display: flex; align-items: center; gap: 1rem; width: 100%; padding: 8px 16px;
  border: 1px solid transparent; border-radius: 10px; background: transparent;
  color: #fff; cursor: pointer; text-align: left; font-family: inherit;
  transition: background 0.15s, border-color 0.15s;
}
/* Hover-Kachel bewusst ohne Rand — nur die Fläche hebt sich ab */
.app-switch-item:hover {
  background: rgba(255, 255, 255, 0.05);
}
.app-switch-item.is-current { background: rgba(255, 255, 255, 0.05); cursor: default; }
.app-switch-item__icon {
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 2rem; height: 2rem; border-radius: 0.5rem; background: #fff; color: #000;
  font-family: 'PF Videotext', 'PFVideoText', sans-serif; font-weight: 500; font-size: 1rem;
}
.app-switch-item.is-current .app-switch-item__icon { background: #8b5cf6; color: #fff; }
.app-switch-item__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.app-switch-item__name { font-size: 0.9rem; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.app-switch-item__desc { font-size: 0.72rem; color: #b0b0b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.app-switch-fill { transition: width 0.2s ease; }
.app-switch-close .app-switch-fill { width: 3.5px; }
/* Icon-Spalten-Swap ohne Flackern: Die App-eigene Corner-Spalte verschwindet
   beim Öffnen INSTANT (die App-Regeln faden mit 0.15s — hier übersteuert),
   weil die Panel-Icon-Spalte an exakt derselben Position übernimmt. Beim
   Schließen erscheint sie erst wieder, wenn das Panel zu ist (Delay 0.24s
   > 0.22s Breiten-Animation) — nie doppelte oder springende Icons. */
body.app-switch-open .header-corner,
body.app-switcher-open .header-corner {
  opacity: 0; visibility: hidden; pointer-events: none; transition: none;
}
body:not(.app-switch-open):not(.app-switcher-open) .header-corner {
  transition: opacity 0s 0.24s, visibility 0s 0.24s;
}

/* ── Schmale Screens (Handy/Tablet) ──────────────────────────────────────
   1. Das angedockte Panel schiebt die Seite per margin-left zur Seite. Auf
      einem 390px-Handy sind das 82vw = 320px — von der App blieben ~70px
      übrig. Dort überlagert das Panel die Oberfläche stattdessen; ein Tap
      daneben schließt es (Listener in initAppSwitcher).
   2. Die 1px-Trennlinie an der Icon-Spalten-Kante sitzt bei ~62px. Sobald
      der Inhalt nicht mehr rechts davon beginnt (zentrierter 800px-Container
      unter ~940px Viewport), schneidet sie quer durch Fragetext und Karten —
      auf schmalen Screens entfällt sie deshalb. */
@media (max-width: 1024px) {
  body.app-switch-open, body.app-switcher-open { margin-left: 0; }
  .app-switch-rail-line { display: none; }
}

/* dock: false (opt-in pro App, z. B. Papers) — KEINE Backticks in diesem
   Kommentar: die CSS steht in einem Template-Literal, ein Backtick wuerde es
   beenden. Das Panel schiebt die Seite
   NIE zur Seite, sondern legt sich in jeder Breite darueber — beim Oeffnen
   springt also kein Element. Gilt zusaetzlich zur Regel oben, die dasselbe
   fuer schmale Screens ohnehin erzwingt. */
body.app-switch-ueberlagernd.app-switch-open,
body.app-switch-ueberlagernd.app-switcher-open { margin-left: 0; }

/* Das Apps-Popover sitzt rechts NEBEN dem Panel (left = Panelbreite + 12px).
   Auf dem Handy begänne es bei ~332px und stünde zu 3/4 außerhalb des
   Bildschirms — dort legt es sich stattdessen über das Panel, unterhalb der
   Kopfzeile mit dem Brand-Namen. */
@media (max-width: 768px) {
  .app-switch-apps {
    left: 8px;
    right: 8px;
    top: calc(1rem + 8px + 2.75rem);
    width: auto;
  }
}

/* Skin 'transparent' (opt-in pro App, z. B. Papers): statt fixer schwarzer
   Flaeche eine hauchzarte Toenung + Haarlinie; Farben folgen den App-Tokens.
   Fallbacks halten die Regeln harmlos, falls eine App die Variablen nicht
   definiert. */
.app-switch-panel.app-switch-panel--transparent {
  background: var(--panel-tint, rgba(0, 0, 0, 0.03));
  border-right: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
  box-shadow: none;
  backdrop-filter: blur(2px);
  color: var(--text-primary, #fff);
}
.app-switch-panel--transparent .app-switch-brand-name,
.app-switch-panel--transparent .app-switch-close,
.app-switch-panel--transparent .app-switch-action,
.app-switch-panel--transparent .app-switch-action__icon {
  color: var(--text-primary, #fff);
}
.app-switch-panel--transparent .app-switch-action:hover {
  background: var(--accent-weak, rgba(255, 255, 255, 0.05));
}
/* Die Fortschrittszeile trug die Farben des dunklen Panels: Spur und Text in
   Weiss mit Alpha, die Fuellung in reinem Weiss. Auf dem hellen, fast
   durchsichtigen Skin ergab das Weiss auf Weiss — der Balken war gerendert,
   aber unsichtbar. Wie die uebrigen Panel-Elemente folgt er jetzt den
   App-Tokens und stimmt damit in beiden Themes. */
.app-switch-panel--transparent .app-switch-progress__bar {
  background: var(--border-color, rgba(255, 255, 255, 0.16));
}
.app-switch-panel--transparent .app-switch-progress__bar > i {
  background: var(--text-primary, #fff);
}
.app-switch-panel--transparent .app-switch-progress__text {
  color: var(--text-secondary, rgba(255, 255, 255, 0.72));
}
.app-switch-rail-line--transparent {
  background: var(--border-color, rgba(255, 255, 255, 0.1));
}

/* Knoepfe, die es nur in der Touch-Bodenleiste gibt (Apps, touchBar-Aktionen,
   Zelle fuer das Profil-Badge). Sie haengen immer im DOM der .header-corner,
   stehen am Desktop aber nie im Weg. */
.app-switch-touch-btn,
.app-switch-touch-cell { display: none; }
.app-switch-touch-btn {
  padding: 0; border: none; background: transparent;
  color: var(--text-primary, #fff); cursor: pointer;
}
.app-switch-touch-btn svg { display: block; width: 1.2rem; height: 1.2rem; overflow: visible; }

/* ── Touch-Geraete: die Leiste geht nach unten ───────────────────────────
   Umschalter ist das Eingabegeraet, nicht die Breite (Spec 2026-09-09, §2):
   ein schmal gezogenes Desktop-Fenster behaelt die Leiste links.
   Nachtrag 12.09.2026: Die Leiste faehrt NICHT mehr aus. Es gibt kein Sheet
   mit ausgeschriebenen Zeilen, keinen Sidepanel-Knopf; an seiner Stelle
   oeffnet ein Apps-Knopf direkt die App-Liste. Alle Knoepfe samt Profil-Badge
   stehen in gleich breiten Zellen und verteilen sich mit gleichen Abstaenden. */
@media (hover: none) and (pointer: coarse) {
  body {
    padding-bottom: calc(var(--mednos-bottombar-h) + env(safe-area-inset-bottom, 0px));
  }

  .header-corner {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
    gap: 0;
    height: var(--mednos-bottombar-h);
    box-sizing: content-box;
    padding: 0 0 env(safe-area-inset-bottom, 0px);
    /* Radiologys Wert; Study und Papers stehen auf 100 und wuerden sonst
       unter App-Overlays rutschen. */
    z-index: 10000;
    /* Deckend im Seitengrund der App (22.09.2026): weiss im hellen, schwarz
       im dunklen Modus. Halbtransparent mit Weichzeichner schien der Inhalt
       darunter durch. Radiology kennt kein --bg-primary, dort greift sein
       stets schwarzes --dark-bg. */
    background: var(--bg-primary, var(--dark-bg, #000));
    border-top: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
  }

  /* Der Sidepanel-Knopf hat unten nichts mehr zu oeffnen. */
  .header-corner .app-switch-btn,
  .header-corner .app-switcher-btn {
    display: none;
  }

  /* Study versteckt sein Zahnrad in der Spalte bewusst (es wird ueber das
     Panel bedient). In der Bodenleiste ist Platz dafuer, und der Nutzer hat
     ausdruecklich alle Buttons unten gewuenscht. */
  .header-corner__settings {
    display: inline-flex;
  }

  /* Gleich breite Zellen fuer JEDEN sichtbaren Knopf der Leiste — erst damit
     sind bei space-evenly nicht nur die Luecken, sondern auch die Abstaende
     der Icon-Mitten gleich. 2.75rem haelt die Mindest-Trefferflaeche von
     44px. SPEZIFITAET (0,4,0), bewusst: die App-Regeln setzen ihren Knoepfen
     1.6rem Breite und display:none (Studys Zahnrad) auf (0,1,0). [hidden]
     bleibt ausgenommen, sonst stuende eine unsichtbare touchBar-Aktion als
     leere Zelle in der Verteilung. */
  .header-corner > :not([hidden]):not(.app-switch-btn):not(.app-switcher-btn) {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    box-sizing: border-box;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  /* Ohne Initialen ist das Badge leer und unsichtbar — seine Zelle darf dann
     keinen Platz in der Verteilung belegen. */
  .header-corner > .app-switch-touch-cell:has(> .profile-badge:empty) {
    display: none;
  }

  /* Kein Sheet mehr: das Panel existiert auf Touch nicht. */
  .app-switch-panel {
    display: none;
  }

  /* Markiert die Kante der Icon-SPALTE, die es unten nicht gibt. */
  .app-switch-rail-line {
    display: none;
  }

  /* Sitzt am Desktop rechts NEBEN dem Panel. Auf Touch gibt es kein Panel —
     die Liste legt sich ueber den Inhalt, oberhalb der Bodenleiste. */
  .app-switch-apps {
    left: 8px;
    right: 8px;
    top: auto;
    width: auto;
    bottom: calc(var(--mednos-bottombar-h) + env(safe-area-inset-bottom, 0px) + 12px);
    max-height: calc(100dvh - var(--mednos-bottombar-h) - 4rem);
    overflow-y: auto;
    transform: translateY(8px);
  }
  .app-switch-apps.is-open {
    transform: translateY(0);
  }
}
`;

function isProd() {
  return location.hostname === 'mednos.eu' || location.hostname.endsWith('.mednos.eu');
}

// Offen-Zustand: Domain-Cookie in Produktion (gilt für alle *.mednos.eu),
// localStorage im lokalen Dev (dort gibt es keine gemeinsame Domain).
function readOpenFlag() {
  if (isProd()) return document.cookie.split(';').some((c) => c.trim().startsWith(`${OPEN_FLAG}=1`));
  try { return localStorage.getItem(OPEN_FLAG) === '1'; } catch { return false; }
}
function writeOpenFlag(on) {
  if (isProd()) {
    document.cookie = on
      ? `${OPEN_FLAG}=1; Domain=.mednos.eu; Path=/; SameSite=Lax; Max-Age=86400`
      : `${OPEN_FLAG}=; Domain=.mednos.eu; Path=/; SameSite=Lax; Max-Age=0`;
  } else {
    try { on ? localStorage.setItem(OPEN_FLAG, '1') : localStorage.removeItem(OPEN_FLAG); } catch { /* egal */ }
  }
}
// Nur in Produktion: lokal gibt es keine gemeinsame Domain, und der
// Auth-Server, der das Cookie liest, ist ohnehin nicht im Spiel.
function writeLastApp(appKey) {
  if (!isProd()) return;
  document.cookie =
    `${LAST_APP_FLAG}=${encodeURIComponent(appKey)}; Domain=.mednos.eu; ` +
    'Path=/; SameSite=Lax; Max-Age=31536000';
}

function esc(s) {
  const d = document.createElement('div');
  d.textContent = s == null ? '' : s;
  return d.innerHTML;
}
function initialOf(name) {
  return (String(name || '').replace(/^Mednos\s+/i, '').charAt(0) || 'M').toUpperCase();
}

/**
 * @param {{
 *   appKey: 'radiology' | 'study' | 'papers',
 *   trigger?: string,
 *   actions?: Array<{ icon: string, label: string, onClick: () => void,
 *                     isVisible?: () => boolean, progress?: boolean, touchBar?: boolean }>,
 *   topNudge?: string,
 *   skin?: 'transparent',
 *   dock?: boolean,
 * }} opts
 * `topNudge` (CSS-Länge, Default '0px') verschiebt die Panel-Icon-Spalte
 * vertikal, um app-spezifische Corner-Offsets zu treffen.
 * `skin: 'transparent'` (opt-in, z. B. Papers) ersetzt die fixe schwarze
 * Panel-Fläche durch eine Tönung aus den App-Tokens; ohne `skin` bleibt
 * das Panel unverändert dunkel.
 * `dock: false` (opt-in, z. B. Papers) lässt das Panel in JEDER Breite
 * überlagern statt ab 1024px anzudocken — die App verschiebt sich beim Öffnen
 * dann nicht. Ohne die Angabe bleibt das Docking wie bisher.
 * Eine Aktion mit `touchBar: true` steht auf Touch-Geraeten zusaetzlich als
 * Icon-Knopf in der Bodenleiste — fuer Aktionen, die die App dort sonst
 * nirgends anbietet (auf Touch gibt es kein ausfahrbares Panel).
 */
export function initAppSwitcher(opts) {
  const self = APPS[opts.appKey];
  if (!self) throw new Error(`Unbekannte App: ${opts.appKey}`);

  // Diese App ist ab jetzt die "zuletzt geöffnete" — mednos.eu leitet nach
  // dem nächsten Login hierher. Beide Apps rufen initAppSwitcher beim Start
  // auf, deshalb braucht es keine zusätzliche Call-Site.
  //
  // ABER: nicht schreiben, solange das Dokument nur PRERENDERT wird. Sobald
  // das Apps-Popup offen ist, speculatePrerender() (weiter unten) rendert die
  // jeweils andere App im Hintergrund komplett vor (Caddy setzt dafür
  // `Supports-Loading-Mode: credentialed-prerender` auf beiden App-Hosts) —
  // diese Vorschau führt initAppSwitcher({appKey: <andere App>}) bereits aus,
  // OBWOHL der User nur das Popup geöffnet, aber nie gewechselt hat. Ein
  // ungebremstes writeLastApp würde das Cookie also schon beim bloßen
  // Anschauen der Liste auf die andere App umschreiben und den nächsten
  // Login in die falsche App leiten. Erst wenn die Vorschau tatsächlich
  // aktiviert wird (prerenderingchange), war der User wirklich hier.
  // `document.prerendering` ist in Browsern ohne die Prerender-API
  // `undefined` — das fällt korrekt in den else-Zweig.
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => writeLastApp(opts.appKey), { once: true });
  } else {
    writeLastApp(opts.appKey);
  }

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  // Docked-Layout: der Body bekommt bei offenem Panel margin-left in
  // Panelbreite (animiert über die Host-Klasse).
  document.body.classList.add('app-switch-host');

  // `dock: false`: überlagern statt schieben, in jeder Breite.
  const ueberlagertImmer = opts.dock === false;
  if (ueberlagertImmer) document.body.classList.add('app-switch-ueberlagernd');

  // Fallback, wenn die Registry nicht erreichbar ist: alle anderen bekannten
  // Apps, umgebungsabhängig verlinkt (nie localhost-URLs in Produktion).
  const fallbackApps = Object.values(APPS)
    .filter((a) => a !== self && !a.pilot)
    .map((a) => ({ name: a.name, url: isProd() ? a.prodUrl : a.devUrl, beschreibung: a.beschreibung }));

  const CURRENT = { name: self.name, desc: 'Aktuell geöffnet', current: true, initial: initialOf(self.name) };

  // „Mednos Radiology" → „Mednos" weiß + Suffix in der App-Akzentfarbe
  const suffix = self.name.replace(/^Mednos\s+/i, '');
  // Ohne `accent` bleibt der Suffix farblos und erbt die Panel-Textfarbe.
  const brandHtml = self.accent
    ? `Mednos <span style="color:${self.accent}">${esc(suffix)}</span>`
    : `Mednos ${esc(suffix)}`;

  // GET ohne Custom-Header: bewusst eine "simple" CORS-Anfrage (kein
  // Content-Type, kein sonstiger Header) — die bleibt preflight-frei, ganz
  // unabhängig davon, was der Auth-Server in seinen allow_headers erlaubt.
  function loadApps() {
    return fetch(REGISTRY_URL, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((apps) => apps.filter((a) => !a.url.includes(self.host)))
      .catch(() => fallbackApps);
  }

  // Apps-Popup: schwebendes Popover rechts neben dem Panel (kein Titel —
  // die "Apps"-Zeile im Panel benennt es bereits)
  const appsPanel = document.createElement('aside');
  appsPanel.className = 'app-switch-apps';
  appsPanel.innerHTML = '<nav class="app-switch-list"></nav>';
  const panel = document.createElement('aside');
  panel.className = 'app-switch-panel';
  // Vertikal-Feinausrichtung der Icon-Spalte auf die App-Corner (z. B.
  // Radiology sitzt bewusst 0.8mm tiefer als Study).
  panel.style.setProperty('--app-switch-nudge', opts.topNudge || '0px');
  panel.innerHTML =
    '<div class="app-switch-panel__inner">' +
    '<div class="app-switch-panel__head">' +
    `<span class="app-switch-brand-name">${brandHtml}</span>` +
    `<button type="button" class="app-switch-close" aria-label="Schließen">${ICON}</button></div>` +
    '<nav class="app-switch-actions">' +
    // "Apps" als erste Aktions-Zeile (Grid-Icon + Label) — togglet das Apps-Panel
    '<button type="button" class="app-switch-action app-switch-action--apps" aria-expanded="false" title="Apps anzeigen">' +
    `<span class="app-switch-action__icon">${GRID_ICON}</span>` +
    '<span class="app-switch-action__label">Apps</span></button>' +
    '</nav>' +
    '</div>';
  // Permanente Trennlinie der Icon-Spalte (auch bei geschlossenem Panel)
  const railLine = document.createElement('div');
  railLine.className = 'app-switch-rail-line';
  // Skin 'transparent': Panel schmiegt sich in das App-Theme, statt als
  // fixe schwarze Flaeche zu stehen. Opt-in pro App; Default unveraendert.
  if (opts.skin === 'transparent') {
    panel.classList.add('app-switch-panel--transparent');
    railLine.classList.add('app-switch-rail-line--transparent');
  }
  document.body.appendChild(railLine);
  document.body.appendChild(appsPanel);
  document.body.appendChild(panel);

  const listEl = appsPanel.querySelector('.app-switch-list');

  // App-spezifische Aktionen (Einstellungen, Prozesse, …): breite Buttons
  // mit Icon links + Label; das Panel bleibt beim Klick offen. Sie werden
  // HINTER der fest eingebauten "Apps"-Zeile angehängt (kein innerHTML —
  // das würde die Apps-Zeile überschreiben).
  //
  // Eine Aktion mit `progress: true` bekommt zusätzlich eine Fortschritts-
  // zeile UNTER dem Knopf (Balken + Text) und wird dafür samt Knopf in eine
  // Gruppe gepackt — die Gruppe ist dann das Flex-Kind, damit der Balken am
  // Knopf klebt statt eine volle Zeilenlücke darunter zu stehen. Ohne das
  // Flag ändert sich am Markup nichts, Radiology und Study bleiben also
  // unberührt.
  const actions = opts.actions || [];
  const actionsEl = panel.querySelector('.app-switch-actions');
  actionsEl.insertAdjacentHTML(
    'beforeend',
    actions
      .map((a, i) => {
        const knopf =
          `<button type="button" class="app-switch-action" data-i="${i}">` +
          `<span class="app-switch-action__icon">${a.icon || ''}</span>` +
          `<span class="app-switch-action__label">${esc(a.label)}</span></button>`;
        if (!a.progress) return knopf;
        return (
          `<div class="app-switch-action-group" data-i="${i}">${knopf}` +
          '<div class="app-switch-progress" hidden>' +
          '<span class="app-switch-progress__bar"><i></i></span>' +
          '<span class="app-switch-progress__text"></span></div></div>'
        );
      })
      .join('')
  );

  /**
   * Fortschritt einer `progress`-Aktion setzen. `anteil` null = unbestimmt
   * (wandernder Balken), 0..1 = echter Stand. `busy` dreht zusätzlich das
   * Icon. Ohne `busy` und ohne `text` verschwindet die Zeile wieder.
   * @param {string} id
   * @param {{ busy?: boolean, anteil?: number|null, text?: string }} zustand
   */
  function setActionProgress(id, zustand = {}) {
    const i = actions.findIndex((a) => a.id === id);
    if (i < 0) return;
    const gruppe = actionsEl.querySelector(`.app-switch-action-group[data-i="${i}"]`);
    if (!gruppe) return;
    const { busy = false, anteil = null, text = '' } = zustand;
    const box = gruppe.querySelector('.app-switch-progress');
    const balken = gruppe.querySelector('.app-switch-progress__bar');
    gruppe.querySelector('.app-switch-action').classList.toggle('is-busy', busy);
    box.hidden = !busy && !text;
    balken.classList.toggle('is-unbestimmt', anteil == null);
    balken.firstElementChild.style.width =
      anteil == null ? '' : `${Math.round(Math.min(Math.max(anteil, 0), 1) * 100)}%`;
    gruppe.querySelector('.app-switch-progress__text').textContent = text;
  }
  actionsEl.addEventListener('click', (e) => {
    const b = e.target.closest('.app-switch-action');
    // Die Apps-Zeile hat ihren eigenen Toggle-Handler (unten)
    if (!b || b.classList.contains('app-switch-action--apps')) return;
    actions[+b.dataset.i]?.onClick?.();
  });

  // Bedingte Zeilen: eine Aktion mit `isVisible()` erscheint nur, wenn die App
  // sie gerade anbietet (z. B. „Session beenden" nur während einer laufenden
  // Lernsession). Bewusst bei JEDEM Öffnen ausgewertet statt über Events
  // synchronisiert — hinter einem offenen Panel kann sich der Zustand nicht
  // ändern, und die App muss nichts abmelden.
  function refreshActionVisibility() {
    actions.forEach((a, i) => {
      if (typeof a.isVisible !== 'function') return;
      // Bei einer Aktion mit Fortschrittszeile muss die GRUPPE weg, sonst
      // bliebe der Balken einer versteckten Aktion stehen.
      const el =
        actionsEl.querySelector(`.app-switch-action-group[data-i="${i}"]`) ||
        actionsEl.querySelector(`.app-switch-action[data-i="${i}"]`);
      if (el) el.hidden = !a.isVisible();
    });
  }
  refreshActionVisibility();

  let rows = [CURRENT];
  function renderList() {
    listEl.innerHTML = rows
      .map(
        (a, i) =>
          `<button class="app-switch-item${a.current ? ' is-current' : ''}" data-i="${i}">` +
          `<span class="app-switch-item__icon">${esc(a.initial)}</span>` +
          `<span class="app-switch-item__text"><span class="app-switch-item__name">${esc(a.name)}</span>` +
          `<span class="app-switch-item__desc">${esc(a.desc)}</span></span></button>`
      )
      .join('');
  }
  renderList();

  // Same-Site-Prerendering (Chrome/Edge): sobald das Apps-Popup offen ist,
  // werden die anderen Apps im Hintergrund komplett vorgerendert — der
  // Wechsel aktiviert dann nur noch die fertige Seite. Voraussetzung ist der
  // Response-Header `Supports-Loading-Mode: credentialed-prerender` auf den
  // App-Hosts (Caddy). Nur in Produktion (localhost: nutzlos, stört Dev) und
  // nur einmal pro Seite injiziert; andere Browser ignorieren das Script.
  let speculated = false;
  function speculatePrerender(urls) {
    if (speculated || !isProd() || !urls.length) return;
    speculated = true;
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({ prerender: [{ urls }] });
    document.head.appendChild(rules);
  }

  function refreshApps() {
    loadApps().then((apps) => {
      rows = [CURRENT].concat(
        apps.map((a) => ({ name: a.name, desc: a.beschreibung || '', url: a.url, initial: initialOf(a.name) }))
      );
      renderList();
      speculatePrerender(apps.map((a) => a.url).filter(Boolean));
    });
  }

  // Apps-Panel-Toggle: die "Apps"-Aktions-Zeile (Grid-Icon + Label) öffnet/
  // schließt das zweite Panel. Der Offen-Zustand wird bewusst NICHT
  // persistiert — das Apps-Panel startet nach einem Seitenwechsel immer
  // geschlossen.
  const brandBtn = panel.querySelector('.app-switch-action--apps');
  let appsOpen = false;
  function openApps() {
    appsOpen = true;
    refreshApps();
    appsPanel.classList.add('is-open');
    brandBtn.setAttribute('aria-expanded', 'true');
    touchAppsBtn?.setAttribute('aria-expanded', 'true');
  }
  function closeApps() {
    appsOpen = false;
    appsPanel.classList.remove('is-open');
    brandBtn.setAttribute('aria-expanded', 'false');
    touchAppsBtn?.setAttribute('aria-expanded', 'false');
  }
  brandBtn.addEventListener('click', () => (appsOpen ? closeApps() : openApps()));

  // ── Touch-Bodenleiste (Nachtrag 12.09.2026 zur Spec 2026-09-09) ─────────
  // Auf Touch faehrt nichts mehr aus: Apps-Knopf vorn, touchBar-Aktionen, das
  // Profil-Badge hinten — alles Icons in einer Reihe. Die Knoepfe haengen
  // immer in der .header-corner; das CSS zeigt sie nur unter TOUCH_MQ.
  const touchMql = window.matchMedia(TOUCH_MQ);
  const istTouch = () => touchMql.matches;
  const corner = document.querySelector('.header-corner');
  const touchAppsBtn = document.createElement('button');
  touchAppsBtn.type = 'button';
  touchAppsBtn.className = 'app-switch-touch-btn app-switch-touch-btn--apps';
  touchAppsBtn.title = 'Apps';
  touchAppsBtn.setAttribute('aria-label', 'Apps öffnen');
  touchAppsBtn.setAttribute('aria-expanded', 'false');
  touchAppsBtn.innerHTML = GRID_ICON;
  touchAppsBtn.addEventListener('click', () => (appsOpen ? closeApps() : openApps()));
  const touchAktionen = actions
    .map((a) => {
      if (!a.touchBar) return null;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'app-switch-touch-btn';
      b.title = a.label;
      b.setAttribute('aria-label', a.label);
      b.innerHTML = a.icon || '';
      b.addEventListener('click', () => a.onClick?.());
      return { a, b };
    })
    .filter(Boolean);
  // Das Badge gehoert profile-panel.js und steht dort fixed unten links. In
  // der Leiste bekommt es eine eigene Zelle, damit es in derselben
  // Zellbreite verteilt wird wie die Knoepfe; ausserhalb von Touch geht es an
  // seinen alten Platz zurueck (DevTools-Emulation kann umschalten).
  const badge = document.getElementById('profileBadge');
  const badgeHeimat = badge ? { parent: badge.parentNode, next: badge.nextSibling } : null;
  const badgeZelle = document.createElement('span');
  badgeZelle.className = 'app-switch-touch-cell';
  if (corner) {
    corner.prepend(touchAppsBtn);
    touchAktionen.forEach(({ b }) => corner.appendChild(b));
    corner.appendChild(badgeZelle);
  }

  // touchBar-Aktionen haengen an App-Zustand (laeuft eine Session?), der sich
  // ohne ein Ereignis aendert, das der Switcher kennt — die Apps muessten
  // sonst jeden Start und jedes Ende melden. Deshalb: sofort nach jedem Klick
  // irgendwo (die meisten Zustandswechsel folgen einem Tap) und zusaetzlich
  // einmal pro Sekunde, solange Touch gilt und die Seite sichtbar ist.
  // isVisible() ist in allen Apps eine billige Abfrage.
  function refreshTouchBar() {
    touchAktionen.forEach(({ a, b }) => {
      b.hidden = typeof a.isVisible === 'function' ? !a.isVisible() : false;
    });
  }
  let touchTakt = null;
  function syncTouch() {
    const touch = istTouch();
    if (badge && corner) {
      if (touch && badge.parentNode !== badgeZelle) {
        badgeZelle.appendChild(badge);
      } else if (!touch && badge.parentNode === badgeZelle && badgeHeimat) {
        badgeHeimat.parent.insertBefore(badge, badgeHeimat.next);
      }
    }
    if (touch) {
      // Ein offen gespeichertes Panel (App-Wechsel am Desktop) gibt es auf
      // Touch nicht: nur optisch schliessen, das Cookie bleibt fuer den
      // naechsten Desktop-Besuch unangetastet.
      if (isOpen) {
        isOpen = false;
        BODY_CLASSES.forEach((c) => document.body.classList.remove(c));
        panel.classList.remove('is-open');
      }
      refreshTouchBar();
      if (!touchTakt) {
        touchTakt = setInterval(() => {
          if (document.visibilityState === 'visible') refreshTouchBar();
        }, 1000);
      }
    } else if (touchTakt) {
      clearInterval(touchTakt);
      touchTakt = null;
    }
  }

  // Popup-Verhalten: Klick außerhalb (und außerhalb der Apps-Zeile) schließt
  document.addEventListener('click', (e) => {
    if (istTouch()) setTimeout(refreshTouchBar, 0);
    if (!appsOpen) return;
    if (appsPanel.contains(e.target) || brandBtn.contains(e.target) || touchAppsBtn.contains(e.target)) return;
    closeApps();
  });

  // Beide Body-Klassen: 'app-switch-open' (Radiology-CSS) und
  // 'app-switcher-open' (Study-CSS) — die Apps blenden damit z. B. ihre
  // Header-Icon-Spalte aus, solange das Panel offen ist.
  const BODY_CLASSES = ['app-switch-open', 'app-switcher-open'];
  let isOpen = false;
  function open(instant) {
    // Auf Touch gibt es kein ausfahrbares Panel (Nachtrag 12.09.2026).
    if (istTouch()) return;
    isOpen = true;
    refreshActionVisibility();
    if (instant) {
      panel.classList.add('no-anim');
      document.body.classList.add('app-switch-noanim');
    }
    BODY_CLASSES.forEach((c) => document.body.classList.add(c));
    panel.classList.add('is-open');
    if (instant) {
      // Reflow erzwingen, dann Transitionen wieder aktivieren
      void panel.offsetWidth;
      panel.classList.remove('no-anim');
      document.body.classList.remove('app-switch-noanim');
    }
    writeOpenFlag(true);
  }
  function close() {
    closeApps();
    isOpen = false;
    BODY_CLASSES.forEach((c) => document.body.classList.remove(c));
    panel.classList.remove('is-open');
    writeOpenFlag(false);
  }

  // Programmatischer Zugriff für App-Code (z. B. Panel schließen, bevor ein
  // Overlay geöffnet wird, das sonst unter dem Panel läge).
  window.__mednosSwitcher = { open: () => open(false), close, isOpen: () => isOpen, setActionProgress };

  const trigger = document.querySelector(opts.trigger || '#appSwitchBtn, #appSwitcherBtn');
  if (trigger) trigger.addEventListener('click', () => (isOpen ? close() : open(false)));

  // Auf schmalen Screens überlagert das Panel die Oberfläche (Media-Query
  // oben), statt sie zur Seite zu schieben — dann muss ein Tap daneben es
  // schließen können, sonst bleibt der Inhalt verdeckt. Auf breiten Screens
  // ist das Panel angedockt und ein Klick daneben soll bewusst nichts tun.
  // Bei `dock: false` gilt dasselbe in jeder Breite: das Panel verdeckt dort
  // ebenfalls Inhalt, also muss ein Klick daneben es schließen können.
  const OVERLAY_MQ = '(max-width: 1024px)';
  document.addEventListener('click', (e) => {
    if (!isOpen) return;
    if (!ueberlagertImmer && !window.matchMedia(OVERLAY_MQ).matches) return;
    if (panel.contains(e.target) || appsPanel.contains(e.target)) return;
    if (trigger && trigger.contains(e.target)) return;
    close();
  });
  panel.querySelector('.app-switch-close').addEventListener('click', close);
  listEl.addEventListener('click', (e) => {
    const b = e.target.closest('.app-switch-item');
    if (!b) return;
    const a = rows[+b.dataset.i];
    if (!a || a.current) { close(); return; }
    if (!a.url) return;
    // Abgangs-Fade: Seite kurz Richtung Schwarz ausblenden, dann navigieren —
    // der Wechsel reißt nicht ab. Das Cookie bleibt gesetzt → die Ziel-App
    // rendert das Panel sofort offen (bei aktivem Prerender ist die Ziel-
    // Seite bereits fertig und die Aktivierung augenblicklich).
    document.documentElement.classList.add('app-switch-leaving');
    setTimeout(() => { window.location.href = a.url; }, 150);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (appsOpen) closeApps();
    else if (isOpen) close();
  });

  // App-Wechsel mit offenem Panel: sofort offen rendern, ohne Animation —
  // nicht auf Touch, dort faehrt das Panel nie aus.
  if (readOpenFlag() && !istTouch()) open(true);

  syncTouch();
  touchMql.addEventListener('change', syncTouch);

  return window.__mednosSwitcher;
}
